import { memo, useState, useEffect, useCallback } from "react";
import { Button } from "@core/components";
import { cn } from "@core/utils/cn";
import { icons } from "@core/assets";
import { usePdfTranslate } from "@modules/viewers/pdf/hooks/usePdfTranslate";

type PdfToolbarProps = {
  currentPage: number;
  totalPages: number;
  scale: number;
  rotation: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRotate: () => void;
  onPageChange: (page: number) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  onFullscreen: () => void;
};

export const PdfToolbar = memo(function PdfToolbar({
  currentPage,
  totalPages,
  scale,
  onZoomIn,
  onZoomOut,
  onRotate,
  onPageChange,
  onPrevPage,
  onNextPage,
  onFullscreen,
}: PdfToolbarProps) {
  const { t } = usePdfTranslate();
  const [localPage, setLocalPage] = useState(currentPage.toString());

  useEffect(() => {
    setLocalPage(currentPage.toString());
  }, [currentPage]);

  const handlePageCommit = useCallback(() => {
    const parsed = parseInt(localPage, 10);
    if (!isNaN(parsed)) {
      onPageChange(Math.min(Math.max(parsed, 1), totalPages));
    } else {
      setLocalPage(currentPage.toString());
    }
  }, [localPage, currentPage, totalPages, onPageChange]);

  const handlePageKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") handlePageCommit();
    },
    [handlePageCommit],
  );

  return (
    <div className="flex items-center justify-between bg-white border border-neutral-200 rounded-lg p-[10px] flex-shrink-0">
      {/* Left group */}
      <div className="flex items-center gap-2">
        {/* Zoom controls */}
        <Button
          type="button"
          aria-label={t("toolbar.zoom-out")}
          onClick={onZoomOut}
          leftIcon={icons["zoom-out"]}
          iconStrokeWidth={1.5}
          size="sm"
          variant="secondary"
        />
        <span className="text-primary-700 text-sm font-medium min-w-[44px] text-center">
          {Math.round(scale * 100)}%
        </span>
        <Button
          type="button"
          aria-label={t("toolbar.zoom-in")}
          onClick={onZoomIn}
          leftIcon={icons["zoom-in"]}
          iconStrokeWidth={1.5}
          size="sm"
          variant="secondary"
        />

        <div className="w-px h-5 bg-neutral-200 mx-1" />

        {/* Rotation */}
        <Button
          type="button"
          aria-label={t("toolbar.rotate")}
          onClick={onRotate}
          leftIcon={icons["rotate-ccw"]}
          iconStrokeWidth={1.5}
          size="sm"
          variant="secondary"
        />

        <div className="w-px h-5 bg-neutral-200 mx-1" />

        {/* Page navigation */}
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            aria-label={t("toolbar.prev-page")}
            onClick={onPrevPage}
            disabled={currentPage <= 1}
            leftIcon={icons["arrow-left"]}
            iconStrokeWidth={1.5}
            size="sm"
            variant="secondary"
          />
          <span className="text-neutral-400 text-xs">{t("toolbar.page")}</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={localPage}
            onChange={(e) => setLocalPage(e.target.value)}
            onBlur={handlePageCommit}
            onKeyDown={handlePageKeyDown}
            className={cn(
              "w-[43px] h-[29px] px-2.5 border border-neutral-200 rounded-lg",
              "text-sm text-center text-secondary-900",
              "focus:outline-none focus:border-primary-600",
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            )}
          />
          <span className="text-neutral-400 text-xs">
            / {totalPages > 0 ? totalPages : "—"}
          </span>
          <Button
            type="button"
            aria-label={t("toolbar.next-page")}
            onClick={onNextPage}
            disabled={currentPage >= totalPages}
            leftIcon={icons["arrow-right"]}
            iconStrokeWidth={1.5}
            size="sm"
            variant="secondary"
          />
        </div>
      </div>

      {/* Right group */}
      <div className="flex items-center gap-2">
        {/* Fullscreen */}
        <Button
          type="button"
          aria-label={t("toolbar.fullscreen")}
          onClick={onFullscreen}
          leftIcon={icons["maximize"]}
          iconStrokeWidth={1.5}
          size="sm"
          variant="secondary"
        />
      </div>
    </div>
  );
});
